/**
 * Founder Control Center v2 · Sección 3 · Growth.
 *
 * Orden V2 spec §4 (ordenado por importancia estratégica del founder):
 *   1. SEO            → SEODashboard (FO · NUEVO · read-only · 4 endpoints reales)
 *   2. Content Studio → ContentStudio (FO · canales reales + plan activación AG-04)
 *   3. Competencia    → Placeholder honesto (AG-05 · Sprint A · sin endpoints reales)
 *   4. A/B Testing    → SuperAdmin tab "ab-results" (embed · /superadmin/ab-results)
 *   5. Cupones        → SuperAdmin tab "coupons" (embed · CRUD /superadmin/coupons)
 *   6. Afiliados      → SuperAdmin tab "affiliates" (embed · /superadmin/affiliate-*)
 *   7. Reseñas        → SuperAdmin tab "reviews" (embed · /superadmin/platform-reviews)
 *
 * Fase 4 Sprint Q (Feb 2026):
 *   ✅ NUEVO · SEODashboard reemplaza el Placeholder SEO con datos REALES
 *     consumiendo /api/seo/{sitemap.xml,robots.txt,meta/{slug},related/{slug}}.
 *     0 endpoints nuevos · 0 mocks introducidos.
 *   ✅ Reordenado para que SEO y Content Studio (los dos motores de crecimiento
 *     orgánico) abran la sección antes de las herramientas tácticas.
 *   ✅ Competencia mejora copy con roadmap concreto AG-05 (no inventa mocks).
 *
 * 0 endpoints backend tocados. Solo reorganización + nueva vista read-only.
 */
import {
  MagnifyingGlass, Sparkle, Trophy, Lightning, Tag, Users, Star, ChartBar,
} from "@phosphor-icons/react";
import SuperAdmin from "../../SuperAdmin";
import ContentStudio from "../ContentStudio";
import SEODashboard from "../SEODashboard";
import SubTabs, { useSubTab } from "./SubTabs";

const ITEMS = [
  { key: "seo",         label: "SEO",            icon: MagnifyingGlass },
  { key: "content",     label: "Content Studio", icon: Sparkle },
  { key: "competitors", label: "Competencia",    icon: Trophy },
  { key: "ab",          label: "A/B Testing",    icon: Lightning },
  { key: "coupons",     label: "Cupones",        icon: Tag },
  { key: "affiliates",  label: "Afiliados",      icon: Users },
  { key: "reviews",     label: "Reseñas",        icon: Star },
];

export default function GrowthSection() {
  const [sub, setSub] = useSubTab("seo");
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-5" data-testid="fcc-growth-section">
      <Header />
      <SubTabs items={ITEMS} value={sub} onChange={setSub} testid="growth-subtabs" />
      <div className="pt-2">
        {sub === "seo"         && <SEODashboard />}
        {sub === "content"     && <ContentStudio />}
        {sub === "competitors" && <CompetitorsPlaceholder />}
        {sub === "ab"          && <SuperAdmin embedded forceTab="ab-results" />}
        {sub === "coupons"     && <SuperAdmin embedded forceTab="coupons" />}
        {sub === "affiliates"  && <SuperAdmin embedded forceTab="affiliates" />}
        {sub === "reviews"     && <SuperAdmin embedded forceTab="reviews" />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
        <ChartBar size={11} weight="fill" /> Founder Control Center · Growth
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
        Crece y monetiza
      </h1>
      <p className="text-xs text-[var(--ag-text-dim)] mt-1">
        SEO · Content · Competencia · A/B · Cupones · Afiliados · Reseñas
      </p>
    </div>
  );
}

/**
 * Placeholder honesto para Competencia (AG-05 Sprint A).
 *
 * No introduce mocks. Documenta concretamente qué hará el agente y qué señales
 * va a recolectar, para que el founder sepa exactamente qué esperar.
 */
function CompetitorsPlaceholder() {
  return (
    <div className="space-y-3" data-testid="growth-competitors-placeholder">
      <div className="rounded-lg border border-dashed border-[var(--ag-border)] bg-[var(--ag-surface)] p-8 text-center">
        <Trophy size={32} weight="duotone" className="mx-auto text-[var(--ag-gold)] opacity-80" />
        <h3 className="font-display text-xl font-bold tracking-tighter mt-3">Competidor tracking</h3>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2 max-w-2xl mx-auto">
          Sin datos hoy. Sin mocks. Activación en <strong>Sprint A · AG-05 Competitor Agent</strong>.
        </p>
      </div>
      <div className="rounded border border-[var(--ag-gold)]/25 bg-[var(--ag-gold)]/5 p-4 text-xs">
        <div className="text-[10px] uppercase tracking-wider text-[var(--ag-gold)] font-semibold mb-2">
          Cuando AG-05 esté activo, este panel mostrará
        </div>
        <ul className="space-y-1.5 text-[var(--ag-text-dim)] list-disc list-inside">
          <li>Mapa de competidores directos por ciudad/sector (datos de Google Maps + scraping legal).</li>
          <li>Posicionamiento orgánico: keywords donde un competidor supera a un centro de AutoGlow.</li>
          <li>Alertas de cambio: nuevo competidor en la zona, cambio de precios, nueva ubicación.</li>
          <li>Comparativa de reseñas (rating medio + volumen + sentimiento) contra centros AutoGlow.</li>
        </ul>
      </div>
    </div>
  );
}
